/**
 * `useStageFor`, with a live generation drawn over it.
 *
 * The base hook is a pure function of the blob's entity; a running generation is exactly the thing
 * the blob does not know (see `genBus.ts`). This wrapper reads both and, for an artifact with an
 * active record, replaces the stage: the morph band until the first byte, the real elements
 * streaming in after it, the staggered resolve
 * once the new bake's raster exists, the real `ArtifactDocument` mounted UNDERNEATH at resolve
 * start so the canvas fades out over settled pixels — and the pane's own pill copy stays honest
 * (`loading` = Thinking, `writing` = Writing the artifact). Opening an existing artifact returns
 * the base stage directly; generation chrome is never used as an artifact-load animation.
 *
 * FALLBACKS ARE ONE PATH: reduced motion, WebGL missing/lost, or a resolve with no texture (bake
 * gave up, raster failed) all end the same way — the record clears and the base stage shows,
 * which is today's pill-and-crossfade behaviour. Never a stuck band.
 *
 * TWO ENTRY POINTS, ONE MECHANISM. `useStageForX(entity)` is /deck's: it finds the key on the open
 * entity and returns the pane's props. `useGenMorph(key)` is the same machinery for a surface that
 * has no `DeckEntity` to hand — the brand kit's composition stage, which is keyed by a role string
 * (`genBus.GenKey`) and draws its own artifact — and hands back the morph element to mount rather
 * than a stage to spread. `useStageForX` is written on top of it, so there is one copy of the state
 * machine and the kit cannot drift from the deck.
 */
import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import type { ArtifactKind } from '~/design-system/components/v1_5/Preview';
import type { DeckEntity } from '../deckContent';
import {
  genClear,
  genRecord,
  genServerVersion,
  genVersion,
  subscribeGen,
  type GenKey,
  type GenRecord,
} from '../generate/genBus';
import { MorphResolveStage } from '../morph/MorphResolveStage';
import { ARTIFACT_H, ARTIFACT_W, useStageFor, type StageProps } from './artifactStage';
import { StreamingArtifactDocument } from './StreamingArtifactDocument';

/** The tuned default: the generation resolve is the last 2550ms, whatever the wait was. */
const RESOLVE_MS = 2550;
/** The stage's own fade is 240ms; the record lingers a touch longer so the fade has a surface. */
const LINGER_MS = 320;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export interface GenMorphOpts {
  /**
   * The finished artifact, mounted UNDER the morph canvas at resolve start so the veil fades out over
   * settled pixels. A caller with nothing settled yet passes nothing and gets the plain resolve.
   */
  settled?: ReactNode;
  /**
   * Hold the band for the WHOLE run, not just the wait before the first byte.
   *
   * /deck hands the wait over to `StreamingArtifactDocument`, which draws the real elements as they
   * arrive, so the band standing down there reveals something better. A surface with no streaming shell
   * has nothing behind it, so standing down reveals an empty mat with a pill on it — Guri, 2026-09-04:
   * "it should be till it finish the generation not only for 2 second". The kit is that surface.
   *
   * Off by default, because on /deck this would cover the streaming document it was designed to yield to.
   */
  bandWhileStreaming?: boolean;
}

export interface GenMorphView {
  /** The live record, or undefined when this key has no generation running. */
  record: GenRecord | undefined;
  /** The pane's word for what is happening, or null when there is nothing to say. */
  status: 'loading' | 'writing' | 'failed' | null;
  /**
   * The morph, for the states where the surface has nothing of its own to show: the wait before the
   * first byte, and the resolve. Null while bytes are arriving (the caller draws the partial), when
   * the record is failed or absent, and for the whole session once `morphless` is true.
   */
  morph: ReactNode | null;
  /** WebGL missing/lost on this machine, or reduced motion: this surface never mounts the morph. */
  morphless: boolean;
  /** End the record. The failed board's Try again calls this before re-running the work. */
  clear: () => void;
}

/**
 * The generation state machine for one key, independent of what the surface draws.
 *
 * Every hook here runs unconditionally on a null key too — a surface with nothing open still has to
 * keep its subscription and its GL/reduced-motion answers between renders.
 */
export function useGenMorph(key: GenKey | null, opts?: GenMorphOpts): GenMorphView {
  useSyncExternalStore(subscribeGen, genVersion, genServerVersion);

  // Flipped once by MorphCanvas when GL is not available on this machine/session; from then on the
  // wrapper stops mounting the morph at all and the plain pill path carries every generation.
  const [glFallback, setGlFallback] = useState(false);
  const reduced = useRef(prefersReducedMotion());

  const record = key !== null ? genRecord(key) : undefined;
  const morphless = glFallback || reduced.current;

  /**
   * The no-texture resolve: nothing for the morph to reveal, so the record ends now and the base
   * stage (which already reads the committed blob) simply shows. An effect, not a render-time call
   * — clearing a store mid-render re-enters every subscriber.
   */
  useEffect(() => {
    if (key === null || !record) return;
    if (record.state === 'resolving' && (morphless || !record.to)) {
      const timer = window.setTimeout(() => genClear(key), 0);
      return () => window.clearTimeout(timer);
    }
  }, [key, record, morphless]);

  const onResolved = useCallback(() => {
    if (key === null) return;
    window.setTimeout(() => genClear(key), LINGER_MS);
  }, [key]);

  const clear = useCallback(() => {
    if (key !== null) genClear(key);
  }, [key]);

  if (key === null || !record) return { record: undefined, status: null, morph: null, morphless, clear };

  if (record.state === 'failed') return { record, status: 'failed', morph: null, morphless, clear };

  const status = record.state === 'waiting' ? 'loading' : 'writing';

  // Bytes arriving is where the two surfaces genuinely differ. /deck streams the real elements into a
  // shell, so the band yielding to them is an upgrade. A caller with no shell has nothing to yield TO,
  // and yielding there is what made the loader look like it lasted two seconds and quit.
  // A textureless resolve still mounts, exactly as before: the effect above ends it on the next tick
  // and one path handles every fallback.
  const streamingStates = opts?.bandWhileStreaming
    ? record.state === 'streaming' || record.state === 'finishing'
    : false;
  const showsMorph =
    !morphless && (record.state === 'waiting' || record.state === 'resolving' || streamingStates);

  return {
    record,
    status,
    morphless,
    clear,
    // The wait before the first byte (`waiting`, the model thinking) is the morph band, never a blank
    // white card, and the landing (`resolving`) is the morph resolve. 98038dbe banded the whole wait;
    // bfafe451 replaced all of it with the shell and left this gap white, which is what this restores.
    morph: showsMorph
      ? React.createElement(
          MorphResolveStage,
          {
            width: ARTIFACT_W,
            height: ARTIFACT_H,
            phase:
              record.state === 'resolving' ? 'resolve' : record.from ? 'morph-out' : 'band',
            fromTexture: record.from,
            toTexture: record.to,
            partsMap: record.parts,
            resolveMs: RESOLVE_MS,
            onResolved,
            onFallback: () => setGlFallback(true),
          },
          record.state === 'resolving' ? opts?.settled ?? null : null,
        )
      : null,
  };
}

export interface StageForXOpts {
  /** Re-run a failed generation (genBus kept the instruction). Wired to the failed board. */
  retryGeneration?: (slideId: number) => void;
  /**
   * What the "Nothing open" board should offer when there is no entity — the directory's current
   * kind, translated. Ignored the moment something IS open, which is why it is an option and not a
   * second argument: it describes the absence, not the item.
   */
  emptyKind?: ArtifactKind;
}

export function useStageForX(entity: DeckEntity | undefined, opts?: StageForXOpts): StageProps {
  const base = useStageFor(entity, opts?.emptyKind);
  const slideId = entity?.kind === 'artifacts' ? entity.slide.id : null;
  // The real document, mounted beneath the canvas exactly at resolve start: the blob has the commit
  // by now, so `base.children` IS the settled ArtifactDocument on the fast bake path.
  // `bandWhileStreaming`: the loader holds until the document has LANDED. Guri, 2026-09-08, watching a
  // six-image rewrite: "that loader was not working. After the loader finished, it was still showing
  // that writing kind of thing, and the whole artifact was white. The loader stays until the design
  // inside the artifact is finished." The streaming shell painted the half-written document (white
  // until its first element), which read as the loader quitting early. So on /deck the band now covers
  // streaming and finishing too, and the resolve reveals the finished artifact. The shell is kept for
  // the machine with no GL, where there is no band to hold.
  const gen = useGenMorph(slideId, { settled: base.children, bandWhileStreaming: true });
  const record = gen.record;

  if (slideId === null) return base;

  // Existing artifacts are content, not work in progress: a selection change returns the base
  // stage directly. Generation chrome is mounted only when the generation bus has an active record.
  if (!record) return base;

  if (record.state === 'failed') {
    return {
      ...base,
      status: 'failed',
      onRetry: () => {
        gen.clear();
        opts?.retryGeneration?.(slideId);
      },
    };
  }

  const status = record.state === 'waiting' ? 'loading' : 'writing';

  // Bytes are arriving. With the band holding (see `bandWhileStreaming` above) the morph is what shows;
  // the streaming shell, the real elements flashed on arrival (bfafe451), is only the fallback for a
  // machine that cannot draw the band. The bus replays the drained portion to a late subscriber, so
  // mounting the shell late loses nothing.
  if ((record.state === 'streaming' || record.state === 'finishing') && !gen.morph) {
    return { ...base, status, children: React.createElement(StreamingArtifactDocument, {
      key: `${slideId}-${record.startedAt}`, slideId,
      title: record.title || 'Artifact',
      seed: record.operation === 'edit' && entity?.kind === 'artifacts' ? entity.slide.docHtml : undefined,
    }) };
  }

  // No GL, reduced motion, or a resolve with no texture: white card + pill for the wait, then the
  // commit lands and base flips to ready on its own. The record clears via the resolve effect.
  if (!gen.morph) return { ...base, status };

  return { ...base, status, children: gen.morph };
}
